import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../core/store';
import { isCoveringLive, toStartDateEndDateString } from './utils/dateRangeHelpers';
import { selectDefaultActivityDateRange } from '../main-map/slices/defaultActivityDateRangeSlice';

/**
 * Selector that returns the formatted start and end date string from the analysis date range
 * Uses the toStartDateEndDateString helper method to format the dates
 */
export const selectAnalysisDateRangeString = createSelector(
    (state: RootState) => state.featureDateRangeSlice.analysisDateRange.dateRange,
    selectDefaultActivityDateRange,
    (dateRange, defaultDateRange) => {
        if (!dateRange?.start || !dateRange?.end) {
            return '';
        }

        // Convert DateValue to Date objects for the helper function
        const startDate = new Date(dateRange.start.year, dateRange.start.month - 1, dateRange.start.day);
        const originalEndDate = new Date(dateRange.end.year, dateRange.end.month - 1, dateRange.end.day - 1);

        // Get the default activity date range end date
        const defaultEndDate = defaultDateRange ? new Date(defaultDateRange.endDate) : new Date();

        // Check if end date is covering live and adjust if needed
        let adjustedEndDate = originalEndDate;
        if (isCoveringLive(originalEndDate, defaultEndDate)) {
            // Set to the last day of the defaultEndDate month
            adjustedEndDate = new Date(defaultEndDate.getFullYear(), defaultEndDate.getMonth() + 1, 0);
        }

        return toStartDateEndDateString(startDate, adjustedEndDate);
    }
);

/**
 * Selector that returns whether the analysis date range end date is covering live (in current month)
 */
export const selectIsAnalysisDateRangeCoveringLive = createSelector(
    (state: RootState) => state.featureDateRangeSlice.analysisDateRange.dateRange,
    selectDefaultActivityDateRange,
    (dateRange, defaultDateRange) => {
        if (!dateRange?.end) {
            return false;
        }

        // Convert DateValue to Date object
        const endDate = new Date(dateRange.end.year, dateRange.end.month - 1, dateRange.end.day - 1);

        // Get the default activity date range end date
        const defaultEndDate = defaultDateRange ? new Date(defaultDateRange.endDate) : new Date();

        return isCoveringLive(endDate, defaultEndDate);
    }
);
